import { logger } from 'helpers/logger'

/**
 * ReorderManager - A centralized orchestrator for content reordering.
 *
 * Solves race conditions caused by:
 * - Multiple rapid reorder clicks before saves complete
 * - Parallel upsertContent calls triggering intermediate Redux state updates
 * - hybridContent normalization clobbering in-flight order changes
 * - Stale closures reading old pageContent state
 *
 * Architecture:
 * - Maintains its own canonical order map (id -> order) as source of truth
 * - Queues reorder operations and processes them sequentially
 * - Batches pending saves and waits for completion before processing next
 * - Provides a snapshot of current orders for the UI to render
 */

interface ReorderOperation {
    contentId: string
    direction: number // -1 = up, 1 = down
    resolve: (result: ReorderResult) => void
}

interface ReorderResult {
    success: boolean
    error?: string
    updatedItems?: { id: string, order: number }[]
}

type SaveFn = (items: any[]) => Promise<any>

interface ReorderManagerConfig {
    onOrderChanged: (updatedItems: { id: string, order: number }[]) => void
    saveFn: SaveFn
}

export class ReorderManager {
    private orderMap: Map<string, number> = new Map()
    private itemMap: Map<string, any> = new Map()
    private queue: ReorderOperation[] = []
    private processing = false
    private config: ReorderManagerConfig
    private debounceTimer: ReturnType<typeof setTimeout> | null = null
    private pendingSaves: Map<string, number> = new Map() // id -> new order
    private savePromise: Promise<any> | null = null

    constructor(config: ReorderManagerConfig) {
        this.config = config
    }

    /**
     * Initialize/sync the manager with the current content array.
     * Call this when pageContent is first derived or after a full refresh.
     * Will NOT overwrite if there are pending operations.
     */
    sync(items: any[]) {
        if (this.processing || this.queue.length > 0 || this.pendingSaves.size > 0) {
            logger.info(`[Reorder] sync skipped - busy (processing=${this.processing} queue=${this.queue.length} pending=${this.pendingSaves.size})`)
            return
        }
        logger.info(`[Reorder] sync ${items?.length} items`)
        this.orderMap.clear()
        this.itemMap.clear()
        items?.forEach((item) => {
            if (item.id) {
                this.orderMap.set(item.id, item.order)
                this.itemMap.set(item.id, item)
            }
        })
    }

    /**
     * Request a reorder. Returns a promise that resolves when the operation
     * (including save) is complete.
     */
    reorder(contentId: string, direction: number): Promise<ReorderResult> {
        return new Promise((resolve) => {
            logger.info(`[Reorder] queued id=${contentId?.substring(0, 8)} dir=${direction} qLen=${this.queue.length}`)
            this.queue.push({ contentId, direction, resolve })
            this.processQueue()
        })
    }

    /**
     * Get current canonical orders for all items.
     */
    getOrders(): Map<string, number> {
        return new Map(this.orderMap)
    }

    /**
     * Get items sorted by current canonical order.
     */
    getSortedItems(): any[] {
        const items = Array.from(this.itemMap.values())
        return items.sort((a, b) => {
            const orderA = this.orderMap.get(a.id) ?? a.order
            const orderB = this.orderMap.get(b.id) ?? b.order
            if (orderA !== orderB) return orderA - orderB
            return String(a.id).localeCompare(String(b.id))
        })
    }

    /**
     * Check if the manager has pending operations.
     */
    isBusy(): boolean {
        return this.processing || this.queue.length > 0 || this.pendingSaves.size > 0
    }

    /**
     * Force clear all state (for unmount/page change).
     */
    reset() {
        this.queue = []
        this.processing = false
        this.pendingSaves.clear()
        this.orderMap.clear()
        this.itemMap.clear()
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer)
            this.debounceTimer = null
        }
    }

    private async processQueue() {
        if (this.processing) return
        this.processing = true

        while (this.queue.length > 0) {
            const op = this.queue.shift()!
            const result = this.executeSwap(op.contentId, op.direction)
            if (result.success && result.updatedItems) {
                // Accumulate pending saves
                result.updatedItems.forEach((item) => {
                    this.pendingSaves.set(item.id, item.order)
                })

                // Notify UI immediately for optimistic update
                this.config.onOrderChanged(result.updatedItems)
            }
            op.resolve(result)
        }

        // After processing all queued operations, flush saves
        if (this.pendingSaves.size > 0) {
            await this.flushSaves()
        }

        this.processing = false

        // Check if more operations arrived while we were saving
        if (this.queue.length > 0) {
            this.processQueue()
        }
    }

    private executeSwap(contentId: string, direction: number): ReorderResult {
        const sorted = this.getSortedItems()

        const currentIdx = sorted.findIndex((x) => x.id === contentId)
        if (currentIdx < 0) {
            logger.warn(`[Reorder] swap - item not found: ${contentId?.substring(0, 8)}`)
            return { success: false, error: 'Item not found' }
        }

        const swapIdx = direction < 0 ? currentIdx - 1 : currentIdx + 1
        if (swapIdx < 0 || swapIdx >= sorted.length) {
            logger.warn(`[Reorder] swap - boundary: ${contentId?.substring(0, 8)} idx=${currentIdx} dir=${direction} len=${sorted.length}`)
            return { success: false, error: 'Cannot move further in this direction' }
        }

        const currentItem = sorted[currentIdx]
        const swapItem = sorted[swapIdx]

        const currentOrder = this.orderMap.get(currentItem.id) ?? currentItem.order
        const swapOrder = this.orderMap.get(swapItem.id) ?? swapItem.order

        logger.info(`[Reorder] swap ${currentItem.id?.substring(0, 8)}:${currentOrder}↔${swapItem.id?.substring(0, 8)}:${swapOrder} dir=${direction}`)

        // If orders are the same (shouldn't happen after normalization, but safety)
        if (currentOrder === swapOrder) {
            // Normalize all and then swap positions
            sorted.forEach((item, idx) => {
                this.orderMap.set(item.id, idx)
            })
            // Re-derive after normalization
            return this.executeSwap(contentId, direction)
        }

        // Swap the orders
        this.orderMap.set(currentItem.id, swapOrder)
        this.orderMap.set(swapItem.id, currentOrder)

        // Update itemMap with new orders
        this.itemMap.set(currentItem.id, { ...currentItem, order: swapOrder })
        this.itemMap.set(swapItem.id, { ...swapItem, order: currentOrder })

        logger.info(`[Reorder] swap result: ${currentItem.id?.substring(0, 8)}→${swapOrder}, ${swapItem.id?.substring(0, 8)}→${currentOrder}`)

        return {
            success: true,
            updatedItems: [
                { id: currentItem.id, order: swapOrder },
                { id: swapItem.id, order: currentOrder },
            ]
        }
    }

    private async flushSaves() {
        const saveItems: any[] = []

        this.pendingSaves.forEach((order, id) => {
            const item = this.itemMap.get(id)
            if (item) {
                saveItems.push({ ...item, order })
            }
        })

        logger.info('[Reorder] flushSaves:', saveItems.map(x => `${x.id?.substring(0, 8)}→order:${x.order}`).join(', '))

        this.pendingSaves.clear()

        try {
            this.savePromise = this.config.saveFn(saveItems)
            await this.savePromise
            logger.info('[Reorder] flushSaves complete')
        } catch (e) {
            logger.error('[Reorder] ReorderManager.flushSaves - Save failed:', e)
        } finally {
            this.savePromise = null
        }
    }
}
